import React, { FunctionComponent, memo, useCallback } from "react";
import { useIntl } from "react-intl";
import { TbCircleFilled, TbCircleDotted, TbCircle } from "react-icons/tb";
import {
  Flex,
  Icon,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { isEqual } from "lodash";

import { IAlgorithmReportResult } from "./types";
import { useControlUI } from "../ControlUIContext";


interface IProps {
  algorithm_name: string;
  result: IAlgorithmReportResult;
  size?: number;
  trueColor?: string;
  falseColor?: string;
  nullColor?: string;
}


const AlgorithmResult: FunctionComponent<IProps> = memo(function AlgorithmResult({
  algorithm_name,
  result,
  size = 24,
  nullColor = undefined,
  trueColor = "enposol",
  falseColor = "red",
}) {
  const intl = useIntl();
  const { expandedReports } = useControlUI();
  const trueBulbColor = useColorModeValue(`${trueColor}.300`, `${trueColor}.500`);
  const falseBulbColor = useColorModeValue(`${falseColor}.500`, `${falseColor}.500`);
  const stepLabel = useCallback((name: string): string => {
    switch (name) {
      case "_peak_shaving": return intl.formatMessage({
        id: "algorithm__step__peak_shaving",
        defaultMessage: "Peak shaving",
      });
      case "_arbitrage_charging": return intl.formatMessage({
        id: "algorithm__step___arbitrage_charging",
        defaultMessage: "Arbitrage charging",
      });
      case "_arbitrage_discharging": return intl.formatMessage({
        id: "algorithm__step___arbitrage_discharging",
        defaultMessage: "Arbitrage discharging",
      });
      case "_outside_arbitrage_window": return intl.formatMessage({
        id: "algorithm__step___outside_arbitrage_window",
        defaultMessage: "End of arbitrage",
      });
      case "_pv_charging": return intl.formatMessage({
        id: "algorithm__step___pv_charging",
        defaultMessage: "Charging from PV",
      });
      case "_chp_charging": return intl.formatMessage({
        id: "algorithm__step___chp_charging",
        defaultMessage: "Charging from CHP",
      });
      case "_discharging_empty_battery": return intl.formatMessage({
        id: "algorithm__step___discharging_empty_battery",
        defaultMessage: "Battery empty",
      });
      case "_charging_full_battery": return intl.formatMessage({
        id: "algorithm__step___charging_full_battery",
        defaultMessage: "Battery full",
      });
      case "_dis_charge_battery": return intl.formatMessage({
        id: "algorithm__step___dis_charge_battery",
        defaultMessage: "Power battery",
      });
      case "_energy_in_battery": return intl.formatMessage({
        id: "algorithm__step___energy_in_battery",
        defaultMessage: "Energy in battery",
      });
      case "_cheap_electricity_from_gas": return intl.formatMessage({
        id: "algorithm__step___cheap_electricity_from_gas",
        defaultMessage: "Cheap gas electricity",
      });
      case "_battery_full": return intl.formatMessage({
        id: "algorithm__step___battery_full",
        defaultMessage: "Battery full",
      });
      case "_space_in_battery": return intl.formatMessage({
        id: "algorithm__step___space_in_battery",
        defaultMessage: "Space in battery",
      });
      case "_cheap_enough_to_sell": return intl.formatMessage({
        id: "algorithm__step___cheap_enough_to_sell",
        defaultMessage: "Cheap enough to sell",
      });
      case "_unavailable_prices": return intl.formatMessage({
        id: "algorithm__step___unavailable_prices",
        defaultMessage: "Prices available",
      });
      case "_positive_buy_negative_sell": return intl.formatMessage({
        id: "algorithm__step___positive_buy_negative_sell",
        defaultMessage: "Negative sell price",
      });
      case "_negative_buy": return intl.formatMessage({
        id: "algorithm__step___negative_buy",
        defaultMessage: "Negative buy price",
      });
      case "_positive_sell": return intl.formatMessage({
        id: "algorithm__step___positive_sell",
        defaultMessage: "Positive sell price",
      });
      case "_arbitrage_charging_and_negative_sell": return intl.formatMessage({
        id: "algorithm__step___arbitrage_charging_and_negative_sell",
        defaultMessage: "Charge negative sell",
      });
      case "_charge_battery": return intl.formatMessage({
        id: "algorithm__step___charge_battery",
        defaultMessage: "Charge battery",
      });
      default: return name;
    }
  }, [intl]);

  return <>
    <Tooltip
      label={expandedReports ? null : stepLabel(result.step)}
      placement="top"
      hasArrow
    >
    <Flex
      direction="row"
      alignItems="center"
      key={algorithm_name}
    >
      <Icon
        boxSize={5}
        color={nullColor}
        mr={result.outcome === null ? 0 : -5}
      >
        {result.outcome === null ? <TbCircleDotted size={size} /> : <TbCircle size={size} />}
      </Icon>
      { result.outcome !== null && (
        <Icon
          boxSize={5}
          color={result.outcome ? trueBulbColor : falseBulbColor}
          p={.5}
        >
          <TbCircleFilled size={size} />
        </Icon>
      )}
      { expandedReports && (
        <Text fontSize="xs" whiteSpace="pre" overflow="hidden" textOverflow="ellipsis"> {stepLabel(result.step).toUpperCase()}</Text>
      )}
    </Flex>
    </Tooltip>
  </>
}, isEqual);

export default AlgorithmResult;
